import pinia from '@/store';
import useMenuStore from '@/store/menu';
import { throttle } from 'lodash-es';

const menuStore = useMenuStore(pinia);

const registerAdditionalTizenKeys = () => {
  console.log('############# trying to map keys');
  const TizenKeys = ['MediaPlay', 'MediaPause', 'MediaPlayPause', 'MediaStop', 'MediaFastForward', 'MediaRewind'];
  const tizen = (window as any).tizen;
  try {
    TizenKeys.forEach(key => {
      tizen.tvinputdevice.registerKey(key);
    });
    console.log('Keys registered successfully');
  } catch (error) {
    console.error('Error registering keys:', error);
  }
};

type FocusableElement = HTMLElement & {
  focus?: () => void;
  click?: () => void;
};

interface MousePos {
  x: number;
  y: number;
}

type FocusManager = {
  moveLeft: () => void;
  moveRight: () => void;
  moveUp: () => void;
  moveDown: () => void;
  enter: () => void;
  back?: () => void;
  play?: () => void;
  setFocus: (el: FocusableElement) => void;
  clearFocus: () => void;
  getCurFocusEl: () => FocusableElement | null;
  setMousePos: (pos: MousePos) => void;
};

// Track if we're in keyboard navigation mode
let lastKeyboardInputTime = 0;
const KEYBOARD_MODE_DURATION = 1000; // Stay in keyboard mode for 1 second after last key press

function createMouseMoveHandler(fm: FocusManager) {
  return throttle((event: MouseEvent) => {
    document.body.style.cursor = 'default';

    fm.setMousePos({
      x: event.clientX,
      y: event.clientY,
    });

    // Don't process mouse events if we're in keyboard navigation mode
    const now = Date.now();
    if (now - lastKeyboardInputTime < KEYBOARD_MODE_DURATION) {
      return;
    }

    const hoveredEl = event.target as HTMLElement;
    const enFocusEl = hoveredEl.closest('[data-focus-type]') as FocusableElement;

    if (enFocusEl) {
      if (enFocusEl.dataset.focusType === 'menu') {
        // Menu hover open behavior removed - menu will now only open via explicit left/back actions
      } else {
        if (menuStore.isExpand) {
          menuStore.setMenuExpand(false);
        }
        fm.setFocus(enFocusEl);
      }
    } else {
      if (menuStore.isExpand) {
        if ((event.target as HTMLElement).closest('.menuBar_container') === null) {
          menuStore.setMenuExpand(false);
        }
      } else {
        fm.clearFocus();
      }
    }
  }, 100);
}

export function initRemoteControl(
  mode: 'lg-pointer' | 'webos' | 'vidaa' | 'tizen' | 'web',
  focusManager: FocusManager,
) {
  console.log('############ initRemoteControla ', mode);
  // VIDAA uses the same key codes as WebOS — treat it identically.
  const normalizedMode = mode === 'vidaa' ? 'webos' : mode;
  switch (normalizedMode) {
    case 'lg-pointer':
      // Only hide cursor on actual LG device, not in browser testing
      if (window.webOS || window.PalmSystem) {
        document.body.style.cursor = 'none';

        document.addEventListener('mousemove', () => {
          document.body.style.cursor = 'default';
        });
      }

      document.addEventListener('click', (e: MouseEvent) => {
        const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
        if (el) el.click();
      });
      break;
    case 'webos': {
      // Only hide cursor on actual WebOS device, not in browser testing
      if (window.webOS || window.PalmSystem) {
        document.body.style.cursor = 'none';
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        const keyCode = e.keyCode || e.which;

        // Debug logging to diagnose physical remote vs mobile app differences
        console.log('🚨 [REMOTE] Key:', keyCode, 'Type:', e.type, 'Repeat:', e.repeat, 'Time:', Date.now());

        if (process.env.NODE_ENV === 'development') {
          console.log(`Key pressed: ${keyCode} (${e.key})`);
        }

        const VOLUME_KEY_CODES = [447, 448, 449, 10182];
        if (!VOLUME_KEY_CODES.includes(keyCode)) {
          e.preventDefault();
        }

        // Mark that we're in keyboard navigation mode
        lastKeyboardInputTime = Date.now();

        switch (keyCode) {
          case 37: // ArrowLeft
            focusManager.moveLeft();
            break;
          case 38: // ArrowUp
            focusManager.moveUp();
            break;
          case 39: // ArrowRight
            focusManager.moveRight();
            break;
          case 40: // ArrowDown
            focusManager.moveDown();
            break;
          case 13: // Enter/OK
            focusManager.enter();
            break;
          case 8: // Backspace
          case 461: // LG Back
          case 10009: // Samsung Back
            focusManager.back?.();
            break;
        }
      };
      document.addEventListener('keydown', handleKeyDown, { passive: false });
      break;
    }
    case 'tizen': {
      // Only hide cursor on actual Tizen device, not in browser testing
      if (window.tizen || window.webapis) {
        document.body.style.cursor = 'none';
      }

      const tizen = (window as any).tizen;
      registerAdditionalTizenKeys();

      if (tizen?.tvinputdevice?.getSupportedKeys && tizen?.tvinputdevice?.registerKey) {
        try {
          const supportedKeys = tizen.tvinputdevice.getSupportedKeys();

          supportedKeys.forEach((key: { name: string; code: number }) => {
            try {
              const VOLUME_KEY_CODES = [447, 448, 449, 10182];
              if (VOLUME_KEY_CODES.includes(key.code)) {
                return;
              }
              tizen.tvinputdevice.registerKey(key.name);
            } catch (e) {
              console.warn(`registerKey failed for ${key.name}:`, e);
            }
          });
        } catch (err) {
          console.warn('Failed to get or register supported keys:', err);
        }
      }

      const handleKeyDown = (e: KeyboardEvent) => {
        const keyCode = e.keyCode || e.which;
        const VOLUME_KEY_CODES = [447, 448, 449, 10182];
        if (!VOLUME_KEY_CODES.includes(keyCode)) {
          e.preventDefault();
        }

        // Mark that we're in keyboard navigation mode
        lastKeyboardInputTime = Date.now();

        switch (keyCode) {
          case 37: // ArrowLeft
            focusManager.moveLeft();
            break;
          case 38: // ArrowUp
            focusManager.moveUp();
            break;
          case 39: // ArrowRight
            focusManager.moveRight();
            break;
          case 40: // ArrowDown
            focusManager.moveDown();
            break;
          case 13: // Enter/OK
            focusManager.enter();
            break;
          case 10009: // Return
          case 8: // Backspace
            focusManager.back?.();
            break;
        }
      };

      document.addEventListener('keydown', handleKeyDown, { passive: false });
      break;
    }
    case 'web': {
      document.addEventListener(
        'keydown',
        throttle((e: KeyboardEvent) => {
          const keyCode = e.keyCode || e.which;
          e.preventDefault();

          // Mark that we're in keyboard navigation mode
          lastKeyboardInputTime = Date.now();

          switch (keyCode) {
            case 37: // ArrowLeft
              focusManager.moveLeft();
              break;
            case 38: // ArrowUp
              focusManager.moveUp();
              break;
            case 39: // ArrowRight
              focusManager.moveRight();
              break;
            case 40: // ArrowDown
              focusManager.moveDown();
              break;
            case 13: // Enter/OK
              focusManager.enter();
              break;
            case 8: // Backspace
            case 461: // LG Back
            case 10009: // Samsung Back
              focusManager.back?.();
              break;
          }
        }, 0),
        { passive: false },
      );

      document.addEventListener('mousemove', createMouseMoveHandler(focusManager));
      break;
    }
  }
}
